import { supabase } from '@/lib/supabase';

export type ReviewStatus = 'AUTO_MAPPED' | 'MAPPED_NEEDS_REVIEW' | 'NEEDS_MAPPING_REVIEW' | 'HUMAN_CONFIRMED' | 'HUMAN_CORRECTED' | 'FAILED';

export type LessonReview = {
  id: string;
  tenant_id: string;
  recording_ref: string;
  media_mapping_id: string | null;
  machine_prediction: Record<string, unknown>;
  machine_confidence: number | null;
  machine_evidence: unknown[];
  discrepancies: unknown[];
  predicted_course_id: string | null;
  predicted_module_id: string | null;
  predicted_lesson_id: string | null;
  suggested_title: string | null;
  summary: string | null;
  cohort: string | null;
  instructor: string | null;
  recording_date: string | null;
  duration_seconds: number | null;
  status: ReviewStatus;
  processing_stage: string | null;
  processing_error: string | null;
  human_final_course_id: string | null;
  human_final_module_id: string | null;
  human_final_lesson_id: string | null;
  human_final_title: string | null;
  decision_type: string | null;
  reviewed_at: string | null;
  decision_history: unknown[];
  created_at: string;
  media_mapping?: { library_url?: string; config?: Record<string, any> } | null;
};

export async function listLessonReviews(history = false): Promise<LessonReview[]> {
  let query = supabase
    .from('major_high_lesson_reviews')
    .select('*, media_mapping:media_mapping_id(library_url,config)')
    .order(history ? 'reviewed_at' : 'created_at', { ascending: false });
  query = history
    ? query.in('status', ['HUMAN_CONFIRMED', 'HUMAN_CORRECTED'])
    : query.in('status', ['AUTO_MAPPED', 'MAPPED_NEEDS_REVIEW', 'NEEDS_MAPPING_REVIEW', 'FAILED']);
  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as unknown as LessonReview[];
}

export async function countPendingLessonReviews(): Promise<number> {
  const { count, error } = await supabase
    .from('major_high_lesson_reviews')
    .select('id', { count: 'exact', head: true })
    .in('status', ['AUTO_MAPPED', 'MAPPED_NEEDS_REVIEW', 'NEEDS_MAPPING_REVIEW', 'FAILED']);
  if (error) return 0;
  return count ?? 0;
}

export async function loadReviewCatalog(tenantId: string) {
  const [{ data: courses, error: courseError }, { data: modules, error: moduleError }, { data: lessons, error: lessonError }] = await Promise.all([
    supabase.from('courses').select('id,title,slug,instructor').eq('tenant_id', tenantId).order('title'),
    supabase.from('modules').select('id,course_id,title,slug,"order"').eq('tenant_id', tenantId).order('order'),
    supabase.from('lessons').select('id,module_id,title,"order"').eq('tenant_id', tenantId).order('order'),
  ]);
  if (courseError) throw courseError;
  if (moduleError) throw moduleError;
  if (lessonError) throw lessonError;
  return { courses: courses ?? [], modules: modules ?? [], lessons: lessons ?? [] };
}

export async function decideLessonReview(input: {
  reviewId: string;
  decision: 'APPROVE' | 'CORRECT';
  courseId?: string | null;
  moduleId?: string | null;
  lessonId?: string | null;
  newLessonTitle?: string | null;
  finalTitle?: string | null;
}): Promise<LessonReview> {
  const { data, error } = await supabase.rpc('major_high_decide_lesson_review', {
    p_review_id: input.reviewId,
    p_decision: input.decision,
    p_course_id: input.courseId ?? null,
    p_module_id: input.moduleId ?? null,
    p_lesson_id: input.lessonId ?? null,
    p_new_lesson_title: input.newLessonTitle ?? null,
    p_final_title: input.finalTitle ?? null,
  });
  if (error) throw error;
  return data as LessonReview;
}
